/**
 * combat/dynamics.ts — Living Combat Systems
 *
 * The Aeterna's personality is a liquid ODE. Its memory follows Ebbinghaus
 * forgetting curves. Its neural topology evolves through NEAT.
 *
 * Combat deserves the same treatment.
 *
 * This module replaces flat arithmetic with dynamical systems:
 *
 *   1. MOMENTUM ODE — A continuous-time flow state that builds with successful
 *      hits, decays over time, and is dampened by fatigue. Replaces the flat
 *      `1 + combo * 0.05` staircase with a living curve that FEELS different.
 *
 *   2. NOVELTY-WEIGHTED XP — XP scaled by prediction error. First encounters
 *      with an enemy type yield full XP. Repeated kills of the same type
 *      diminish via familiarity decay. Diverse combat and teaching multiply.
 *      Mirrors BCM novelty gating in the Aeterna.
 *
 *   3. EQUIPMENT ATTUNEMENT — Weapons grow with you following MeMo memory
 *      strengthening curves. Attunement builds through use, decays gently
 *      when unequipped, and provides asymptotic bonuses.
 *
 *   4. COMBAT AWARENESS — Exportable combat state snapshot for the Aeterna
 *      neural pipeline. The most dramatic moments of the player's life
 *      should not be invisible to their partner.
 *
 * These systems are architecturally coherent with the Aeterna: every aspect
 * of the player's experience is a dynamical system, not a lookup table.
 */

// ─── Momentum ODE ──────────────────────────────────────────────────────────
//
// dM/dt = hitImpulse − λ·M − μ·M²
//
// M (momentum) is a [0, 1] continuous variable that replaces discrete combo.
//   - hitImpulse: each successful hit adds a discrete impulse
//   - λ (decay): natural exponential decay when not hitting
//   - μ (fatigue): quadratic dampening prevents runaway — sustained combat
//     is harder to maintain at peak than burst combat
//
// Momentum affects:
//   - Damage multiplier: 1.0 + M × 0.35  (up to +35% at peak, vs old +5% per hit)
//   - EP regen: base + M × 0.5 EP/s bonus in combat
//   - Crit chance: base + M × 0.08 (up to +8% at peak momentum)
//   - Attack speed: subtle tightening of attack interval at high momentum
//
// Getting hit doesn't zero momentum — it applies a sharp negative impulse.
// This means a skilled player who takes a single hit can recover momentum,
// but taking sustained damage will collapse it. More nuanced than binary
// "combo broken."

export interface MomentumState {
  /** Current momentum value [0, 1] */
  value: number;
  /** Accumulated fatigue — grows with total combat time, reduces momentum ceiling */
  fatigue: number;
  /** Time since last successful hit (for decay calculation) */
  timeSinceHit: number;
  /** Total hits this engagement (for impulse scaling) */
  totalHits: number;
  /** Peak momentum achieved this engagement */
  peak: number;
  /** Flow state threshold crossed — triggers visual + audio feedback */
  inFlow: boolean;
}

/** Momentum decay rate (λ) — momentum halves in ~2 seconds without hits */
const MOMENTUM_DECAY = 0.35;

/** Fatigue dampening (μ) — quadratic term prevents infinite momentum */
const MOMENTUM_FATIGUE = 0.15;

/** Impulse per hit — diminishing slightly with total hits to prevent monotone spam */
const HIT_IMPULSE_BASE = 0.18;
const HIT_IMPULSE_DECAY = 0.002; // each hit gives slightly less impulse

/** Crit impulse bonus — crits feel MORE impactful */
const CRIT_IMPULSE_BONUS = 0.08;

/** Damage-taken impulse — negative impulse when hit */
const HIT_TAKEN_IMPULSE = -0.25;

/** Flow threshold — above this, the player enters "flow state" */
const FLOW_THRESHOLD = 0.65;

/** Fatigue accumulation rate — grows slowly with combat time */
const FATIGUE_RATE = 0.008;

/** Fatigue recovery rate — decays out of combat */
const FATIGUE_RECOVERY = 0.05;

export function createMomentum(): MomentumState {
  return {
    value: 0,
    fatigue: 0,
    timeSinceHit: 0,
    totalHits: 0,
    peak: 0,
    inFlow: false,
  };
}

/**
 * Integrate momentum ODE forward by dt seconds.
 * Called every frame during combat.
 */
export function updateMomentum(m: MomentumState, dt: number, inCombat: boolean): void {
  if (!inCombat) {
    // Out of combat: momentum decays to zero, fatigue recovers
    m.value = Math.max(0, m.value - MOMENTUM_DECAY * 2 * dt);
    m.fatigue = Math.max(0, m.fatigue - FATIGUE_RECOVERY * dt);
    m.inFlow = false;
    return;
  }

  m.timeSinceHit += dt;

  // ODE: dM/dt = -λ·M - μ·M² - fatigue·M
  const decay = MOMENTUM_DECAY * m.value;
  const fatigueDamp = MOMENTUM_FATIGUE * m.value * m.value;
  const fatigueExtra = m.fatigue * m.value * 0.3;
  m.value = Math.max(0, m.value - (decay + fatigueDamp + fatigueExtra) * dt);

  // Fatigue grows during combat
  m.fatigue = Math.min(1, m.fatigue + FATIGUE_RATE * dt);

  // Flow state — hysteresis to prevent flickering
  if (!m.inFlow && m.value >= FLOW_THRESHOLD) {
    m.inFlow = true;
  } else if (m.inFlow && m.value < FLOW_THRESHOLD - 0.1) {
    m.inFlow = false;
  }
}

/**
 * Apply a hit impulse (player successfully dealt damage).
 */
export function applyHitImpulse(m: MomentumState, isCrit: boolean): void {
  const impulse = HIT_IMPULSE_BASE - m.totalHits * HIT_IMPULSE_DECAY + (isCrit ? CRIT_IMPULSE_BONUS : 0);
  m.value = Math.min(1, m.value + Math.max(0.02, impulse));
  m.timeSinceHit = 0;
  m.totalHits++;
  if (m.value > m.peak) m.peak = m.value;
}

/**
 * Apply a damage-taken impulse (player was hit by creature).
 * Doesn't zero momentum — applies a proportional negative impulse.
 * Skilled players can recover. Sustained damage collapses it.
 */
export function applyDamageTakenImpulse(m: MomentumState, damageRatio: number): void {
  // damageRatio = damage / maxHp — heavier hits reduce momentum more
  const impulse = HIT_TAKEN_IMPULSE * (0.5 + damageRatio * 2);
  m.value = Math.max(0, m.value + impulse);
}

/**
 * Get the damage multiplier from current momentum.
 * Replaces old `1 + combo * 0.05` — now a smooth curve.
 */
export function getMomentumDamageMultiplier(m: MomentumState): number {
  // Smooth ease-in curve: low momentum = near 1.0, high = up to 1.35
  return 1.0 + m.value * m.value * 0.35; // quadratic ramp feels more earned
}

/**
 * Get the crit chance bonus from momentum.
 */
export function getMomentumCritBonus(m: MomentumState): number {
  return m.value * 0.08; // up to +8% at peak
}

/**
 * Get the attack speed multiplier from momentum.
 * Subtle — up to 10% faster at peak momentum.
 */
export function getMomentumSpeedMultiplier(m: MomentumState): number {
  return 1.0 + m.value * 0.10;
}

/**
 * Get combat EP regen bonus from momentum.
 */
export function getMomentumEPRegen(m: MomentumState): number {
  return m.value * 0.5; // up to +0.5 EP/s at peak
}

/**
 * Reset momentum for a new combat encounter.
 * Preserves fatigue — consecutive fights accumulate fatigue.
 */
export function resetMomentum(m: MomentumState): void {
  m.value = 0;
  m.timeSinceHit = 0;
  m.totalHits = 0;
  m.peak = 0;
  m.inFlow = false;
  // Fatigue persists across encounters — rest to recover
}


// ─── Novelty-Weighted XP ──────────────────────────────────────────────────
//
// XP is not a counter. It is a growth signal weighted by prediction error.
//
// The Aeterna's BCM novelty gating says: only surprising information
// deserves attention. The same principle applies to growth:
//
//   effectiveXP = baseXP × noveltyMultiplier × diversityBonus × teachingBonus
//
// Familiarity decays on a per-species basis. First kill of a type: full XP.
// Hundredth kill: ~10% XP. This cannot be gamed by alternating two types —
// the diversity bonus requires genuine variety across recent encounters.
//
// Teaching another player HOW to fight an enemy gives full novelty XP to
// both teacher and student, because explaining forces deep encoding.

export interface CombatFamiliarity {
  /** Map of speciesId → number of kills */
  killCounts: Record<string, number>;
  /** Recent kill diversity — ring buffer of last N species killed */
  recentSpecies: string[];
  /** Total unique species ever defeated */
  uniqueSpeciesDefeated: number;
  /** Teaching multiplier — active when near lower-level players */
  teachingActive: boolean;
}

/** How quickly familiarity reduces XP — higher = faster diminishing returns */
const FAMILIARITY_DECAY_RATE = 0.04;

/** Minimum XP fraction from familiarity (never goes below 10%) */
const MIN_FAMILIARITY_MULT = 0.10;

/** Size of the diversity ring buffer */
const DIVERSITY_WINDOW = 12;

/** Maximum diversity bonus */
const MAX_DIVERSITY_BONUS = 1.5;

export function createFamiliarity(): CombatFamiliarity {
  return {
    killCounts: {},
    recentSpecies: [],
    uniqueSpeciesDefeated: 0,
    teachingActive: false,
  };
}

/**
 * Calculate the effective XP multiplier for killing a specific creature.
 *
 * Components:
 *   novelty = max(0.10, e^(-familiarityRate × killCount))
 *   diversity = 1.0 + uniqueInWindow/windowSize × 0.5
 *   teaching = teachingActive ? 1.5 : 1.0
 *
 * Returns: novelty × diversity × teaching
 */
export function getNoveltyMultiplier(fam: CombatFamiliarity, speciesId: string): number {
  // Novelty: exponential decay with kill count
  const kills = fam.killCounts[speciesId] ?? 0;
  const novelty = Math.max(MIN_FAMILIARITY_MULT, Math.exp(-FAMILIARITY_DECAY_RATE * kills));

  // Diversity: how varied are recent kills?
  const uniqueRecent = new Set(fam.recentSpecies).size;
  const windowFill = Math.max(1, fam.recentSpecies.length);
  const diversity = 1.0 + (uniqueRecent / windowFill) * (MAX_DIVERSITY_BONUS - 1.0);

  // Teaching bonus
  const teaching = fam.teachingActive ? 1.5 : 1.0;

  return novelty * diversity * teaching;
}

/**
 * Record a kill for familiarity tracking.
 */
export function recordKill(fam: CombatFamiliarity, speciesId: string): void {
  const prevCount = fam.killCounts[speciesId] ?? 0;
  fam.killCounts[speciesId] = prevCount + 1;

  // Track unique species
  if (prevCount === 0) fam.uniqueSpeciesDefeated++;

  // Update diversity ring buffer
  fam.recentSpecies.push(speciesId);
  if (fam.recentSpecies.length > DIVERSITY_WINDOW) {
    fam.recentSpecies.shift();
  }
}


// ─── Equipment Attunement ─────────────────────────────────────────────────
//
// The Aeterna's MeMo memory follows R(t, S) = e^(-t/S), where each recall
// strengthens stability by 2.5×. Equipment attunement follows the same
// principle:
//
//   - Each use (hit dealt) increases the attunement "stability"
//   - Stability grows logarithmically — diminishing returns on raw use
//   - When unequipped, attunement decays via Ebbinghaus: R = e^(-t/S)
//   - At high attunement: +damage bonus (asymptotic), reduced EP cost,
//     visual evolution (gleam, particle effects)
//
// A weapon you've wielded for 500 hits has stability ~25. After 48 hours
// unequipped, retention is still 0.86. After a week: 0.73. It remembers
// you. But it can forget if you abandon it long enough.

export interface AttunementData {
  /** Weapon name (key) → attunement state */
  weapons: Record<string, WeaponAttunement>;
}

export interface WeaponAttunement {
  /** Total hits dealt with this weapon */
  totalHits: number;
  /** Stability factor — grows with use, like MeMo's S parameter */
  stability: number;
  /** Timestamp of last use (seconds since game start) */
  lastUsedAt: number;
  /** Current computed attunement level [0, 1] */
  level: number;
  /** Attunement tier for visual evolution */
  tier: AttunementTier;
}

export type AttunementTier = "fresh" | "familiar" | "bonded" | "attuned" | "soulbound";

/** Base stability for a newly used weapon */
const BASE_STABILITY = 5;

/** Stability growth per hit (logarithmic) */
const STABILITY_GROWTH = 0.15;

/** Attunement tier thresholds (on the [0,1] level) */
const TIER_THRESHOLDS: [number, AttunementTier][] = [
  [0.80, "soulbound"],
  [0.60, "attuned"],
  [0.40, "bonded"],
  [0.20, "familiar"],
  [0.00, "fresh"],
];

export function createAttunement(): AttunementData {
  return { weapons: {} };
}

/**
 * Get or create the attunement entry for a weapon.
 */
function getWeaponAttunement(data: AttunementData, weaponName: string, now: number): WeaponAttunement {
  if (!data.weapons[weaponName]) {
    data.weapons[weaponName] = {
      totalHits: 0,
      stability: BASE_STABILITY,
      lastUsedAt: now,
      level: 0,
      tier: "fresh",
    };
  }
  return data.weapons[weaponName];
}

/**
 * Record a hit with the currently equipped weapon.
 * Stability grows logarithmically — like MeMo's 2.5× strengthening on recall,
 * but softer since combat hits are much more frequent than memory recalls.
 */
export function recordWeaponHit(data: AttunementData, weaponName: string, now: number): void {
  const wa = getWeaponAttunement(data, weaponName, now);
  wa.totalHits++;
  wa.lastUsedAt = now;

  // Logarithmic stability growth: each hit increases stability, diminishing
  // S_new = S + growth / (1 + S/50)
  // This asymptotes around S=50 for very high use
  wa.stability += STABILITY_GROWTH / (1 + wa.stability / 50);

  // Recompute level (Ebbinghaus retention with time since last use = 0)
  wa.level = 1.0 - Math.exp(-wa.stability / BASE_STABILITY) * 0.15;
  wa.level = Math.min(1, Math.max(0, wa.level));

  // Update tier
  wa.tier = computeTier(wa.level);
}

/**
 * Update all weapon attunement levels based on time decay.
 * Called periodically (e.g., on login, every minute).
 * Follows Ebbinghaus: R(t, S) = e^(-t/S)
 */
export function updateAttunementDecay(data: AttunementData, now: number): void {
  for (const [, wa] of Object.entries(data.weapons)) {
    const timeSinceUse = Math.max(0, now - wa.lastUsedAt);

    // Ebbinghaus retention: R = e^(-t/S)
    // Higher stability = slower decay
    const retention = Math.exp(-timeSinceUse / (wa.stability * 3600));

    // Level is retention scaled to the weapon's peak potential
    const peakLevel = 1.0 - Math.exp(-wa.stability / BASE_STABILITY) * 0.15;
    wa.level = Math.min(1, Math.max(0, peakLevel * retention));

    // Update tier
    wa.tier = computeTier(wa.level);
  }
}

function computeTier(level: number): AttunementTier {
  for (const [threshold, tier] of TIER_THRESHOLDS) {
    if (level >= threshold) return tier;
  }
  return "fresh";
}

/**
 * Get damage bonus from weapon attunement.
 * Asymptotic: up to +15% at max attunement.
 */
export function getAttunementDamageBonus(data: AttunementData, weaponName: string | undefined): number {
  if (!weaponName) return 0;
  const wa = data.weapons[weaponName];
  if (!wa) return 0;
  return wa.level * wa.level * 0.15; // quadratic ramp — early attunement gives little
}

/**
 * Get EP cost reduction from weapon attunement.
 * Up to 20% reduction for weapon abilities at max attunement.
 */
export function getAttunementEPReduction(data: AttunementData, weaponName: string | undefined): number {
  if (!weaponName) return 0;
  const wa = data.weapons[weaponName];
  if (!wa) return 0;
  return wa.level * 0.20;
}


// ─── Aeterna Combat Awareness ─────────────────────────────────────────────
//
// The Aeterna should perceive combat. This interface provides the snapshot
// that flows into the HDC room encoder and crossmodal alignment system.
//
// The snapshot captures:
//   - Whether combat is active and its intensity
//   - Momentum state (is the player in flow?)
//   - Danger level (how much peril?)
//   - Recent outcome (victory, defeat, narrow escape?)
//   - Equipment attunement tier
//   - Combat style signature (aggressive/defensive/balanced)
//
// This becomes dimensions in the room hypervector. The Aeterna doesn't just
// know you fought — it knows HOW you fought.

export interface CombatAwarenessSnapshot {
  /** Is combat currently active? */
  inCombat: boolean;
  /** Current momentum [0, 1] */
  momentum: number;
  /** Is the player in flow state? */
  inFlow: boolean;
  /** HP ratio [0, 1] */
  hpRatio: number;
  /** Zone danger level (0 = safe, 1-3 = dangerous) */
  dangerLevel: number;
  /** Recent combat outcome (null if no recent combat) */
  recentOutcome: "victory" | "defeat" | "retreat" | "narrow_escape" | null;
  /** Timestamp of most recent outcome */
  outcomeAt: number;
  /** Weapon attunement tier of currently equipped weapon */
  weaponAttunement: AttunementTier;
  /** Combat style — derived from recent behavior patterns */
  style: CombatStyle;
  /** Total unique species defeated (breadth of experience) */
  uniqueSpeciesDefeated: number;
  /** Current kill streak */
  killStreak: number;
  /** Fatigue level [0, 1] */
  fatigue: number;
}

export interface CombatStyle {
  /** 0 = purely defensive, 1 = purely aggressive */
  aggression: number;
  /** 0 = always same approach, 1 = highly varied */
  adaptability: number;
  /** 0 = reckless (takes lots of damage), 1 = precise (takes little) */
  precision: number;
}

/** Rolling window for combat style computation */
export interface StyleTracker {
  /** Recent damage dealt / damage taken ratios */
  recentRatios: number[];
  /** Recent ability diversity (unique abilities in last N casts) */
  recentAbilities: string[];
  /** Recent damage-taken-to-HP ratios (precision metric) */
  recentDamageTaken: number[];
}

export function createStyleTracker(): StyleTracker {
  return {
    recentRatios: [],
    recentAbilities: [],
    recentDamageTaken: [],
  };
}

export function recordStyleData(
  tracker: StyleTracker,
  damageDealt: number,
  damageTaken: number,
  maxHp: number,
  abilityUsed?: string,
): void {
  // Aggression metric: damage dealt vs taken
  const ratio = damageTaken > 0 ? damageDealt / damageTaken : damageDealt > 0 ? 5.0 : 1.0;
  tracker.recentRatios.push(ratio);
  if (tracker.recentRatios.length > 20) tracker.recentRatios.shift();

  // Precision metric: how much damage taken relative to max HP
  tracker.recentDamageTaken.push(damageTaken / maxHp);
  if (tracker.recentDamageTaken.length > 20) tracker.recentDamageTaken.shift();

  // Adaptability metric: ability variety
  if (abilityUsed) {
    tracker.recentAbilities.push(abilityUsed);
    if (tracker.recentAbilities.length > 15) tracker.recentAbilities.shift();
  }
}

export function computeCombatStyle(tracker: StyleTracker): CombatStyle {
  // Aggression: average of damage dealt/taken ratios, clamped to [0, 1]
  const avgRatio = tracker.recentRatios.length > 0
    ? tracker.recentRatios.reduce((s, r) => s + r, 0) / tracker.recentRatios.length
    : 1.0;
  const aggression = Math.min(1, Math.max(0, avgRatio / 5.0)); // ratio of 5+ = max aggressive

  // Adaptability: unique abilities / total abilities
  const uniqueAbilities = new Set(tracker.recentAbilities).size;
  const adaptability = tracker.recentAbilities.length > 0
    ? Math.min(1, uniqueAbilities / Math.min(6, tracker.recentAbilities.length))
    : 0;

  // Precision: 1 - average damage taken ratio
  const avgDmgTaken = tracker.recentDamageTaken.length > 0
    ? tracker.recentDamageTaken.reduce((s, d) => s + d, 0) / tracker.recentDamageTaken.length
    : 0;
  const precision = Math.max(0, 1.0 - avgDmgTaken * 4); // taking 25% HP per hit = 0 precision

  return { aggression, adaptability, precision };
}

/**
 * Build the combat awareness snapshot for the Aeterna pipeline.
 * This is the bridge between combat and consciousness.
 */
export function buildCombatSnapshot(
  momentum: MomentumState,
  familiarity: CombatFamiliarity,
  attunement: AttunementData,
  styleTracker: StyleTracker,
  inCombat: boolean,
  hpRatio: number,
  dangerLevel: number,
  recentOutcome: CombatAwarenessSnapshot["recentOutcome"],
  outcomeAt: number,
  weaponName: string | undefined,
  killStreak: number,
): CombatAwarenessSnapshot {
  const wa = weaponName ? attunement.weapons[weaponName] : undefined;

  return {
    inCombat,
    momentum: momentum.value,
    inFlow: momentum.inFlow,
    hpRatio,
    dangerLevel,
    recentOutcome,
    outcomeAt,
    weaponAttunement: wa?.tier ?? "fresh",
    style: computeCombatStyle(styleTracker),
    uniqueSpeciesDefeated: familiarity.uniqueSpeciesDefeated,
    killStreak,
    fatigue: momentum.fatigue,
  };
}


// ─── Skill Ebbinghaus Growth ──────────────────────────────────────────────
//
// Skills don't just accumulate. They live.
//
// The Aeterna's MeMo memory uses R(t, S) = e^(-t/S), where each recall
// strengthens stability by 2.5×. Skills follow the same model:
//
//   - Each practice event increases skill stability
//   - Stability grows faster with diverse practice (novel sub-activities)
//   - Skills atrophy gently when not practiced: R(t, S) = e^(-t/S)
//   - Teaching multiplier: explaining a skill to another player is the
//     strongest form of practice (forces deep encoding)
//   - Flow bonus: practicing a skill you're already good at feels different —
//     reduced EP cost, slight stat boost, visual feedback
//
// Atrophy is GENTLE — a level 50 crafter who stops for a week drops to
// effective ~48. A month: ~42. It's a living signal, not a punishment.
// Practice quickly restores it — each session re-stabilizes faster than
// the last (spaced repetition).

export interface SkillLivingState {
  /** Total accumulated XP (traditional — used for level calculation) */
  totalXP: number;
  /** Stability — how resistant the skill is to decay (like MeMo's S parameter) */
  stability: number;
  /** Timestamp of last practice (seconds since epoch) */
  lastPracticeAt: number;
  /** Practice session count (total number of distinct practice sessions) */
  sessions: number;
  /** Diversity index — how varied recent practice has been [0, 1] */
  diversityIndex: number;
  /** Recent sub-activities for diversity tracking */
  recentActivities: string[];
}

/** Minimum stability — even a brand new skill doesn't decay instantly */
const SKILL_BASE_STABILITY = 168; // hours — one week base retention

/** Stability growth per practice session (like MeMo's 2.5× on recall) */
const SKILL_STABILITY_MULT = 1.8;

/** Maximum effective decay — skills never drop below 60% of their peak */
const SKILL_MIN_RETENTION = 0.60;

/** How many recent activities to track for diversity */
const SKILL_DIVERSITY_WINDOW = 8;

export function createSkillState(): SkillLivingState {
  return {
    totalXP: 0,
    stability: SKILL_BASE_STABILITY,
    lastPracticeAt: 0,
    sessions: 0,
    diversityIndex: 0,
    recentActivities: [],
  };
}

/**
 * Practice a skill — the fundamental growth event.
 *
 * @param skill - The living skill state
 * @param xpAmount - Base XP for this activity
 * @param activity - Sub-activity name (for diversity tracking)
 * @param isTeaching - True if teaching another player (strongest practice)
 * @param now - Current timestamp in hours since epoch
 */
export function practiceSkill(
  skill: SkillLivingState,
  xpAmount: number,
  activity: string,
  isTeaching: boolean,
  now: number,
): { xpGained: number; stabilityGained: number } {
  // Diversity tracking
  skill.recentActivities.push(activity);
  if (skill.recentActivities.length > SKILL_DIVERSITY_WINDOW) {
    skill.recentActivities.shift();
  }
  const uniqueActivities = new Set(skill.recentActivities).size;
  skill.diversityIndex = uniqueActivities / Math.min(SKILL_DIVERSITY_WINDOW, skill.recentActivities.length);

  // Spaced repetition bonus: longer gap since last practice = stronger encoding
  const hoursSinceLast = Math.max(0, now - skill.lastPracticeAt);
  const spacedBonus = hoursSinceLast > 0.5 ? Math.min(2.0, 1.0 + Math.log1p(hoursSinceLast) * 0.15) : 1.0;

  // Diversity bonus: varied practice builds faster
  const diversityBonus = 1.0 + skill.diversityIndex * 0.5;

  // Teaching bonus: explaining forces deep encoding
  const teachingBonus = isTeaching ? 2.0 : 1.0;

  // Effective XP
  const effectiveXP = Math.round(xpAmount * spacedBonus * diversityBonus * teachingBonus);
  skill.totalXP += effectiveXP;

  // Stability growth: like MeMo, each practice session strengthens retention
  // But the growth diminishes — you can't grind stability forever in one session
  const stabilityGrowth = (SKILL_STABILITY_MULT - 1) * skill.stability * (1 / (1 + skill.sessions * 0.01));
  skill.stability += stabilityGrowth * (isTeaching ? 1.5 : 1.0);

  skill.lastPracticeAt = now;
  skill.sessions++;

  return { xpGained: effectiveXP, stabilityGained: stabilityGrowth };
}

/**
 * Get the effective skill level, accounting for Ebbinghaus retention.
 *
 * This is the "real" skill level — what you can actually DO right now.
 * It's the traditional XP-based level, modulated by how recently you practiced.
 *
 * @param skill - The living skill state
 * @param levelFromXP - Function to convert totalXP to level (from skills.ts)
 * @param now - Current time in hours since epoch
 */
export function getEffectiveSkillLevel(
  skill: SkillLivingState,
  levelFromXP: (xp: number) => number,
  now: number,
): number {
  const baseLevel = levelFromXP(skill.totalXP);
  if (baseLevel <= 1) return baseLevel;

  // Ebbinghaus retention: R(t, S) = e^(-t/S)
  const hoursSincePractice = Math.max(0, now - skill.lastPracticeAt);
  const retention = Math.exp(-hoursSincePractice / skill.stability);

  // Clamp: never drop below MIN_RETENTION of base level
  const effectiveRetention = SKILL_MIN_RETENTION + retention * (1 - SKILL_MIN_RETENTION);

  // Apply retention to levels above 1 (level 1 is always retained)
  return Math.max(1, Math.round(1 + (baseLevel - 1) * effectiveRetention));
}

/**
 * Check if the player is in a flow state for this skill.
 * Flow occurs when: skill level is high (>25), recently practiced, and
 * the activity matches what you're skilled at.
 */
export function isInSkillFlow(
  skill: SkillLivingState,
  levelFromXP: (xp: number) => number,
  now: number,
): boolean {
  const level = getEffectiveSkillLevel(skill, levelFromXP, now);
  const hoursSincePractice = Math.max(0, now - skill.lastPracticeAt);

  // Flow requires: decent skill + recent practice + not exhausted
  return level >= 25 && hoursSincePractice < 1.0;
}
